// File Storage Utility - Replace Supabase Storage
// Uses /api/db/files endpoint backed by database + optional S3/Blob integration
// Consolidates with generic CRUD API to stay under Vercel function limits

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export const fileStorage = {
  /**
   * Upload a file
   * @param {Object} options - { file, bucket, companyId, fileName }
   * @returns {Promise<{id, file_id, file_name, ...}>}
   */
  async upload({ file, bucket = 'case-documents', companyId, fileName }) {
    if (!file || !companyId) {
      throw new Error('file and companyId required');
    }

    const token = localStorage.getItem('amlguard_token');
    if (!token) {
      throw new Error('Not authenticated');
    }

    const name = fileName || file.name;
    const type = file.type || 'application/octet-stream';
    const fileId = generateUUID();

    try {
      const response = await fetch('/api/db/files', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_id: companyId,
          file_id: fileId,
          file_name: name,
          file_type: type,
          file_path: `${companyId}/${bucket}/${fileId}_${name}`,
          file_size: file.size,
          bucket,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const data = await response.json();
      console.log('[FileStorage] Upload successful:', fileId);
      return data;
    } catch (error) {
      console.error('[FileStorage] Upload error:', error.message);
      throw error;
    }
  },

  /**
   * Download a file (get metadata + signed URL)
   * @param {Object} options - { fileId, companyId }
   * @returns {Promise<{file, signedUrl}>}
   */
  async download({ fileId, companyId }) {
    if (!fileId || !companyId) {
      throw new Error('fileId and companyId required');
    }

    const token = localStorage.getItem('amlguard_token');
    if (!token) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await fetch(
        `/api/db/files?filter=${JSON.stringify({ file_id: fileId, company_id: companyId })}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Download failed');
      }

      const data = await response.json();
      return data[0] || { file: null };
    } catch (error) {
      console.error('[FileStorage] Download error:', error.message);
      throw error;
    }
  },

  /**
   * Delete a file
   * @param {Object} options - { fileId, companyId }
   * @returns {Promise<{deleted}>}
   */
  async delete({ fileId, companyId }) {
    if (!fileId || !companyId) {
      throw new Error('fileId and companyId required');
    }

    const token = localStorage.getItem('amlguard_token');
    if (!token) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await fetch('/api/db/files', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          where: {
            file_id: fileId,
            company_id: companyId,
          },
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Delete failed');
      }

      const data = await response.json();
      console.log('[FileStorage] Delete successful:', fileId);
      return data;
    } catch (error) {
      console.error('[FileStorage] Delete error:', error.message);
      throw error;
    }
  },

  /**
   * List files in a bucket
   * @param {Object} options - { companyId, bucket }
   * @returns {Promise<Array>}
   */
  async list({ companyId, bucket = 'case-documents' }) {
    if (!companyId) {
      throw new Error('companyId required');
    }

    const token = localStorage.getItem('amlguard_token');
    if (!token) {
      throw new Error('Not authenticated');
    }

    try {
      const filter = JSON.stringify({ company_id: companyId, bucket });
      const response = await fetch(`/api/db/files?filter=${encodeURIComponent(filter)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        console.error('[FileStorage] List error:', response.status);
        return [];
      }

      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('[FileStorage] List error:', error.message);
      return [];
    }
  },
};

// Supabase Storage Compatibility Layer - Direct HTTP uploads
export const supabaseStorageCompat = {
  from: (bucket) => ({
    async upload(path, file, options = {}) {
      try {
        const token = localStorage.getItem('amlguard_token');
        if (!token) {
          return { data: null, error: { message: 'Not authenticated' } };
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('path', path);
        formData.append('bucket', bucket);

        const response = await fetch('/api/db/files', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
          return { data: null, error: { message: errorData.error || 'Upload failed' } };
        }

        const data = await response.json();
        return { data: { path }, error: null };
      } catch (err) {
        return { data: null, error: { message: err.message } };
      }
    },
    async download(path) {
      return {
        data: null,
        error: { message: 'Download not yet implemented' },
      };
    },
    async remove(paths) {
      try {
        const token = localStorage.getItem('amlguard_token');
        if (!token) {
          return { data: null, error: { message: 'Not authenticated' } };
        }

        for (const path of paths) {
          await fetch(`/api/db/files?path=${encodeURIComponent(path)}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
          });
        }
        return { data: [], error: null };
      } catch (err) {
        return { data: null, error: { message: err.message } };
      }
    },
    async createSignedUrl(path, expiresIn) {
      return {
        data: { signedUrl: '#' },
        error: null,
      };
    },
  }),
};
